
/**
 * User Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { UserManagementCreateSchema, UserManagementUpdateSchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';
import { logDropdownParamsInvocation } from '../utils/dropdown-params-telemetry.js';
import { withRetry } from '../utils/retry.js';

export class UserService {
    constructor(api, config = {}) {
        this.api = api;
        this.config = config;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await withRetry(
                () => this.api.getAll(params),
                { maxRetries: 2 }
            );

            const body = response;
            return {
                success: true,
                data: body.data || [],
                pagination: body.pagination || {},
                uniqueValues: body.uniqueValues || {},
                focus: body.focus,
                message: body.message || 'User listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch user list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await withRetry(
                () => this.api.getById(id),
                { maxRetries: 2 }
            );

            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch user');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {

        const validation = UserManagementCreateSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.create(payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'User oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create user');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {

        const validation = UserManagementUpdateSchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation(
                'Geçersiz form verisi',
                validation.error.format()
            );
        }
        const payload = validation.data;


        try {
            const response = await this.api.update(id, payload);

            return {
                success: true,
                data: response.data,
                message: response.message || 'User güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update user');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'User silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete user');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            // Use optimized batch delete endpoint
            const response = await this.api.batchDelete(ids);

            return {
                success: true,
                data: response.data,
                message: response.message || `${ids.length} öğe silindi`
            };
        } catch (error) {
            // Fallback to sequential delete if batch endpoint fails
            const results = { success: [], failed: [] };
            for (const id of ids) {
                try {
                    await this.api.delete(id);
                    results.success.push(id);
                } catch (err) {
                    results.failed.push({ id, error: err.message });
                }
            }

            return {
                success: results.failed.length === 0,
                data: results,
                message: `${results.success.length}/${ids.length} öğe silindi`
            };
        }
    }

    /**
     * Get users for dropdown selection
     * @returns {Promise<Object>}
     */
    async getDropdownParams() {
        logDropdownParamsInvocation('UserService');
        try {
            const response = await this.api.client.get(this.api.basePath + '/dropdown/params');
            const data = response.data?.data || [];

            const dropdownData = data.map(d => ({
                value: d.id,
                label: d.name || d.username || d.email
            }));

            return {
                success: true,
                data: dropdownData,
                message: 'Dropdown verisi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch dropdown data');
        }
    }

    /**
     * Custom operation: createChangePassword
     */
    async createChangePassword(id, data = {}) {
        try {
            const response = await this.api.client.post(`/${id}/change-password`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createChangePassword');
        }
    }

    async getLicenseStats() {
        try {
            const response = await this.api.getLicenseStats();
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch license stats');
        }
    }

    async exportCsv(params = {}) {
        try {
            const blob = await this.api.exportCsv(params);
            return { success: true, data: blob };
        } catch (error) {
            throw this._handleError(error, 'Failed to export users');
        }
    }

    async inviteUser(data) {
        try {
            const response = await this.api.invite(data);
            return {
                success: response.success ?? true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to invite user');
        }
    }

    async resendInvite(userId) {
        try {
            const response = await this.api.resendInvite(userId);
            return {
                success: response.success ?? true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to resend invite');
        }
    }

    async batchPatchUsers(data) {
        try {
            const response = await this.api.batchPatch(data);
            return {
                success: response.success ?? true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to batch update users');
        }
    }

    /**
     * Fetch the unit-role assignments associated with this user.
     * @param {string} userId
     */
    async getUnitRoleAssignments(userId) {
        try {
            const response = await this.api.getUnitRoleAssignments(userId);
            return {
                success: response.success ?? true,
                data: response.data || [],
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch unit-role assignments');
        }
    }

    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        // Handle Axios error response (or HttpClientError wrapping axios)
        const axiosLike = error?.originalError ?? error;
        const response = axiosLike?.response ?? error?.response;
        if (response) {
            const status = response.status;
            const data = response.data;
            const message = data?.error || data?.message || defaultMessage;
            const details = data?.details;

            // Only rewrite raw Mongo duplicate messages; keep API-translated ones.
            const messageIsRawDup =
                typeof message === 'string' && /duplicate key|E11000/i.test(message);
            if (messageIsRawDup) {
                const dup = new ApiError(
                    'Bu kullanıcı adı veya e-posta adresi zaten kullanımda.',
                    400,
                    'DUPLICATE_ENTRY',
                );
                dup.details = details || message;
                throw dup;
            }

            const apiError = new ApiError(message, status, data?.code || 'API_ERROR', error);
            apiError.details = details ?? data ?? null;
            if (Array.isArray(data?.conflicts)) {
                apiError.conflicts = data.conflicts;
            }
            throw apiError;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
